import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Bot,
  CheckCircle,
  Lightbulb,
  TrendingUp,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';

interface MessageFeedback {
  type: 'strength' | 'improvement' | 'tip';
  content: string;
}

interface ConversationMessage {
  role: 'client' | 'sales rep';
  content: string;
  timestamp: string;
  feedback: MessageFeedback[];
}

interface SalesFeedbackProps {
  feedback: {
    strengths: string[];
    areasForImprovement: string[];
    actionableNextSteps: Array<{ type: string; description: string }>;
    scenarioOverview: string;
    objectiveAssessment: string;
    metrics: {
      overall: number;
      needsDiscovery: number;
      objectionHandling: number;
      closingEffectiveness: number;
      rapport: number;
    };
    keyInsights: string[];
    conversation: ConversationMessage[];
  };
  onPracticeAgain: () => void;
  onDiscussFeedback: () => void;
}

const ConversationAnalysis = ({
  messages,
}: {
  messages: ConversationMessage[];
}) => {
  // Filter out empty messages
  const validMessages = messages.filter(
    (msg) => msg.content?.trim().length > 0,
  );

  return (
    <div className="space-y-4">
      {validMessages.map((message, idx) => (
        <div
          key={idx}
          className={cn(
            'p-4 rounded-lg transition-colors',
            message.role === 'sales rep' ? 'bg-blue-50' : 'bg-gray-50',
          )}
        >
          <div className="flex items-start gap-3">
            <div className="mt-1">
              {message.role === 'sales rep' ? (
                <User className="h-5 w-5 text-blue-600" />
              ) : (
                <Bot className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-medium">
                  {message.role === 'sales rep' ? 'You' : 'Client'}
                </p>
                <span className="text-xs text-gray-500">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <p className="text-gray-700">{message.content}</p>
            </div>
          </div>

          {message.feedback && message.feedback.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 pl-8 space-y-3"
            >
              {message.feedback.map((feedback, feedbackIdx) => (
                <div key={feedbackIdx} className="flex items-start gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      'flex items-center gap-1',
                      feedback.type === 'strength' &&
                        'bg-green-100 text-green-700 border-green-200',
                      feedback.type === 'improvement' &&
                        'bg-amber-100 text-amber-700 border-amber-200',
                      feedback.type === 'tip' &&
                        'bg-blue-100 text-blue-700 border-blue-200',
                    )}
                  >
                    {feedback.type === 'strength' && (
                      <CheckCircle className="h-3 w-3" />
                    )}
                    {feedback.type === 'improvement' && (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    {feedback.type === 'tip' && (
                      <Lightbulb className="h-3 w-3" />
                    )}
                    {feedback.type}
                  </Badge>
                  <p className="text-sm text-gray-600 flex-1">
                    {feedback.content}
                  </p>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );
};

export default function SalesFeedback({
  feedback,
  onPracticeAgain,
  onDiscussFeedback,
}: SalesFeedbackProps) {
  console.log('SalesFeedback -> feedback:', feedback);
  const [activeTab, setActiveTab] = useState<'overview' | 'conversation'>(
    'overview',
  );

  if (!feedback) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Tab Buttons */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={cn(
            'pb-3 px-4 text-sm font-medium',
            activeTab === 'overview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700',
          )}
        >
          Performance Overview
        </button>
        <button
          onClick={() => setActiveTab('conversation')}
          className={cn(
            'pb-3 px-4 text-sm font-medium',
            activeTab === 'conversation'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700',
          )}
        >
          Conversation Analysis
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' ? (
          <>
            {/* Overview Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Performance Overview
                </h3>
                <p className="text-gray-600">{feedback?.scenarioOverview}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Metrics */}
                <div className="space-y-4">
                  {Object.entries(feedback.metrics).map(([key, value]) => (
                    <div key={key}>
                      <div className="flex justify-between mb-2">
                        <p className="text-sm font-medium text-gray-500">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <span className="text-sm font-medium text-gray-900">
                          {value}%
                        </span>
                      </div>
                      <Progress value={value} className="h-2" />
                    </div>
                  ))}
                </div>

                {/* Key Insights */}
                <div className="space-y-3">
                  {feedback.keyInsights.map((insight, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
                      <p className="text-sm text-gray-600">{insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Areas for Improvement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Areas for Improvement
              </h3>
              <div className="space-y-4">
                {feedback.areasForImprovement.map((area, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg"
                  >
                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                    <p className="text-sm text-gray-600">{area}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Next Steps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Action Steps
              </h3>
              <div className="space-y-4">
                {feedback.actionableNextSteps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg"
                  >
                    {step.type === 'practice' ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    ) : (
                      <TrendingUp className="h-5 w-5 text-blue-500 mt-0.5" />
                    )}
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Conversation Analysis
            </h3>
            <ConversationAnalysis messages={feedback.conversation} />
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button
            variant="outline"
            size="lg"
            onClick={onPracticeAgain}
            className="min-w-[200px]"
          >
            Practice Again
          </Button>
          <Button
            size="lg"
            onClick={onDiscussFeedback}
            className="min-w-[200px]"
            disabled={true}
          >
            Discuss Feedback
          </Button>
        </div>
      </div>
    </div>
  );
}
